import apiClient from './apiClient';
import toast from 'react-hot-toast';

/**
 * Admin Service
 * Handles all admin-related data fetching and operations
 * Now uses API client instead of direct Supabase calls
 */

/**
 * Get dashboard statistics
 * @returns {Promise<Object>} Dashboard stats (user counts, appointment counts, etc.)
 */
export async function getAdminStats() {
  try {
    const stats = await apiClient.getAdminStats();
    return stats;
  } catch (error) {
    // Suppress connection errors (expected when backend is down)
    const errorMessage = error?.message || error?.toString() || '';
    const isConnectionError = errorMessage.includes('Failed to fetch') || 
                              errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                              error instanceof TypeError;
    
    if (!isConnectionError && import.meta.env.DEV) {
      console.warn('Error fetching admin stats:', errorMessage);
    }
    // Return default stats on error
    return {
      totalUsers: 0,
      activeUsers: 0,
      totalPhysicians: 0,
      totalPatients: 0,
      totalSponsors: 0,
      pendingApprovals: 0,
    };
  }
}

/**
 * Get recent system activity
 * @returns {Promise<Array>} Recent activity logs
 */
export async function getRecentActivity(limit = 20) {
  try {
    const activity = await apiClient.getRecentActivity(limit);
    return activity || [];
  } catch (error) {
    // Suppress connection errors
    const errorMessage = error?.message || error?.toString() || '';
    const isConnectionError = errorMessage.includes('Failed to fetch') || 
                              errorMessage.includes('ERR_CONNECTION_REFUSED') ||
                              error instanceof TypeError;
    
    if (!isConnectionError && import.meta.env.DEV) {
      console.warn('Error fetching recent activity:', errorMessage);
    }
    return [];
  }
}

/**
 * Get pending physician applications
 * @returns {Promise<Object>} Pending physicians
 */
export async function getPendingPhysicians() {
  try {
    const data = await apiClient.getPendingPhysicians();
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching pending physicians:', error);
    return { data: [], error };
  }
}

/**
 * Get pending sponsor applications
 * @returns {Promise<Object>} Pending sponsors
 */
export async function getPendingSponsors() {
  try {
    const data = await apiClient.getPendingSponsors();
    return { data, error: null };
  } catch (error) {
    console.error('Error fetching pending sponsors:', error);
    return { data: [], error };
  }
}

/**
 * Approve a physician application
 */
export async function approvePhysician(userId, physicianId) {
  try {
    await apiClient.approvePhysician(userId, physicianId);
    toast.success('Physician approved successfully');
    return { success: true, error: null };
  } catch (error) {
    console.error('Error approving physician:', error);
    toast.error(error.message || 'Failed to approve physician');
    return { success: false, error };
  }
}

/**
 * Reject a physician application
 */
export async function rejectPhysician(userId, physicianId, rejectedBy, reason) {
  try {
    await apiClient.rejectPhysician(userId, physicianId, reason);
    toast.success('Physician application rejected');
    return { success: true, error: null };
  } catch (error) {
    console.error('Error rejecting physician:', error);
    toast.error(error.message || 'Failed to reject physician');
    return { success: false, error };
  }
}

/**
 * Approve a sponsor application
 */
export async function approveSponsor(userId, sponsorId) {
  try {
    await apiClient.approveSponsor(userId, sponsorId);
    toast.success('Sponsor approved successfully');
    return { success: true, error: null };
  } catch (error) {
    console.error('Error approving sponsor:', error);
    toast.error(error.message || 'Failed to approve sponsor');
    return { success: false, error };
  }
}

/**
 * Reject a sponsor application
 */
export async function rejectSponsor(userId, sponsorId, rejectedBy, reason) {
  try {
    await apiClient.rejectSponsor(userId, sponsorId, reason);
    toast.success('Sponsor application rejected');
    return { success: true, error: null };
  } catch (error) {
    console.error('Error rejecting sponsor:', error);
    toast.error(error.message || 'Failed to reject sponsor');
    return { success: false, error };
  }
}

export default {
  getAdminStats,
  getRecentActivity,
  getPendingPhysicians,
  getPendingSponsors,
  approvePhysician,
  rejectPhysician,
  approveSponsor,
  rejectSponsor,
};
